'use client';

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  ExternalLink,
  MapPin,
  MousePointerClick,
  Radio,
  RotateCcw,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AccountPriorityCard } from '@/components/shared/AccountPriorityCard';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { socketIo } from '@/contexts/NotificationProvider';
import { cn } from '@/lib/utils';
import {
  dismissLiveVisitor,
  getLiveVisitors,
  pounceLiveVisitor,
} from '@/services/liveVisitors';

const DEFAULT_OPENER = 'Hi! I noticed you’re checking out our site. Is there anything I can help you with?';

const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${Math.max(0, seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const pageLabel = (page: string | null) => {
  if (!page) return 'Unknown page';
  try {
    const url = new URL(page);
    return `${url.pathname}${url.search}` || '/';
  } catch {
    return page;
  }
};

// A banded Account Priority is coloured by its band; the legacy fit a visitor
// falls back to has no band, so it keeps the threshold ramp it always used.
const fitTone = (score: number | null, label?: string | null) => {
  if (score === null) return 'bg-slate-100 text-slate-600 border-slate-200';
  if (label === 'High') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (label === 'Medium') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (label === 'Low') return 'bg-slate-100 text-slate-600 border-slate-200';
  if (score >= 70) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (score >= 40) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const errorMessage = (error: unknown) => {
  const detail = (error as any)?.response?.data?.error || (error as any)?.response?.data?.message;
  return detail || (error instanceof Error ? error.message : 'Something went wrong.');
};

interface LiveVisitorsPanelProps {
  onPounced: (threadId: string) => void;
}

export default function LiveVisitorsPanel({ onPounced }: LiveVisitorsPanelProps) {
  const vendorId = useWorkspace()?.activeWorkspace?.vendorid;
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [opener, setOpener] = useState(DEFAULT_OPENER);
  const [actionError, setActionError] = useState<string | null>(null);

  const queryKey = useMemo(() => [API_ENDPOINTS.liveVisitors, vendorId], [vendorId]);
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: getLiveVisitors,
    enabled: !!vendorId,
    refetchInterval: 30_000,
  });
  const visitors = data?.visitors || [];
  const selected = visitors.find((visitor) => visitor.visitorId === selectedId) || visitors[0] || null;

  useEffect(() => {
    if (selected?.visitorId !== selectedId) setSelectedId(selected?.visitorId || null);
  }, [selected?.visitorId, selectedId]);

  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey });
    };
    socketIo.on('visitor:live-updated', invalidate);
    return () => {
      socketIo.off('visitor:live-updated', invalidate);
    };
  }, [queryClient, queryKey]);

  const dismissMutation = useMutation({
    mutationFn: dismissLiveVisitor,
    onSuccess: async () => {
      setActionError(null);
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (mutationError) => setActionError(errorMessage(mutationError)),
  });

  const pounceMutation = useMutation({
    mutationFn: ({ visitorId, message }: { visitorId: string; message: string }) =>
      pounceLiveVisitor(visitorId, message),
    onSuccess: async ({ threadId }) => {
      setActionError(null);
      await queryClient.invalidateQueries({ queryKey });
      onPounced(threadId);
    },
    onError: (mutationError) => setActionError(errorMessage(mutationError)),
  });

  const handlePounce = () => {
    if (!selected || !opener.trim()) return;
    setActionError(null);
    pounceMutation.mutate({ visitorId: selected.visitorId, message: opener.trim() });
  };

  return (
    <div className="flex-1 min-h-0 flex overflow-hidden bg-white">
      <aside className="w-[340px] flex-shrink-0 border-r border-slate-200 flex flex-col overflow-hidden">
        <div className="px-4 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Matched visitors</h2>
              <p className="text-xs text-slate-500 mt-0.5">Active in the last {data?.activeWithinMinutes || 5} minutes</p>
            </div>
            <button
              type="button"
              aria-label="Refresh live visitors"
              onClick={() => refetch()}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              disabled={isFetching}
            >
              <RotateCcw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="space-y-2 p-1">
              {[0, 1, 2].map((item) => (
                <div key={item} className="h-28 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-sm font-semibold text-slate-700">Couldn’t load live visitors</p>
              <p className="text-xs text-red-600 mt-2">{errorMessage(error)}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>Try again</Button>
            </div>
          ) : visitors.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
                <Radio className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-slate-700 mt-4">No matched visitors right now</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">New high-priority visitors appear here automatically.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {visitors.map((visitor) => {
                const isSelected = selected?.visitorId === visitor.visitorId;
                // One number per visitor: the Account Priority where the
                // projection has one, the legacy fit where it does not.
                const onPriority = visitor.accountPriority?.score != null;
                const displayedScore = visitor.accountPriority?.score ?? visitor.fitScore;
                return (
                  <button
                    type="button"
                    key={visitor.visitorId}
                    onClick={() => {
                      setSelectedId(visitor.visitorId);
                      setActionError(null);
                    }}
                    className={cn(
                      'w-full text-left rounded-xl border p-3 transition-colors',
                      isSelected ? 'bg-blue-50 border-blue-200' : 'bg-white border-transparent hover:bg-slate-50',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">
                          {visitor.companyName || visitor.companyDomain || 'Identified company'}
                        </p>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{pageLabel(visitor.currentPage)}</p>
                      </div>
                      <span
                        className={cn(
                          'text-xs font-bold px-2 py-1 rounded-full border',
                          fitTone(displayedScore, visitor.accountPriority?.label),
                        )}
                        title={onPriority ? 'Account Priority' : 'GTM Fit — this account has no Account Priority score yet'}
                      >
                        {displayedScore === null ? 'Unknown' : `${displayedScore}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
                      <span className="inline-flex items-center gap-1"><Radio className="w-3 h-3 text-emerald-500" /> {formatDuration(visitor.dwellSeconds)}</span>
                      <span>{visitor.pageViews} page{visitor.pageViews === 1 ? '' : 's'}</span>
                      {visitor.isReturning && <span className="font-semibold text-blue-600">Returning</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto bg-slate-50/60">
        {selected ? (
          <div className="max-w-4xl mx-auto p-6 lg:p-8">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-5">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-xl font-bold text-slate-950 truncate">
                      {selected.companyName || selected.companyDomain || 'Identified company'}
                    </h1>
                    {selected.companyDomain && <p className="text-sm text-slate-500 mt-1">{selected.companyDomain}</p>}
                  </div>
                </div>
                {selected.accountPriority?.score == null && selected.fitScore !== null && (
                  <div className={cn('rounded-xl border px-4 py-2 text-center flex-shrink-0', fitTone(selected.fitScore))}>
                    <div className="text-xl font-black leading-none">{selected.fitScore}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wide mt-1">{selected.fitLabel || 'Fit score'}</div>
                  </div>
                )}
              </div>

              <AccountPriorityCard
                priority={selected.accountPriority || null}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Live activity</h3>
                  <div className="mt-3 space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <MousePointerClick className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800">Current page</p>
                        {selected.currentPage ? (
                          <a className="text-blue-600 hover:underline break-all" href={selected.currentPage} target="_blank" rel="noreferrer">
                            {pageLabel(selected.currentPage)} <ExternalLink className="inline w-3 h-3" />
                          </a>
                        ) : <p className="text-slate-500">Unknown</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <Radio className="w-4 h-4 text-emerald-500" />
                      <span>{formatDuration(selected.dwellSeconds)} on site · {selected.pageViews} page{selected.pageViews === 1 ? '' : 's'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <RotateCcw className="w-4 h-4 text-slate-400" />
                      <span>{selected.isReturning ? 'Returning visitor' : 'First known visit'}</span>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Company</h3>
                  <div className="mt-3 space-y-3 text-sm text-slate-700">
                    <div className="flex items-center gap-3"><Building2 className="w-4 h-4 text-slate-400" /><span>{selected.industry || 'Industry unavailable'}</span></div>
                    <div className="flex items-center gap-3"><Users className="w-4 h-4 text-slate-400" /><span>{selected.employeeRange || 'Company size unavailable'}</span></div>
                    <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-slate-400" /><span>{selected.location || 'Location unavailable'}</span></div>
                  </div>
                </section>
              </div>

              <div className="px-6 pb-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Matched audiences</h3>
                {/* #4544 Phase 3: "why now" -- the same fact/operator/value reason the delivered
                    Slack/Teams card already shows for the same match, not just a bare audience
                    name with no explanation of what actually triggered it. */}
                <div className="mt-3 space-y-2">
                  {selected.whyNow.map((match) => (
                    <div key={match.audienceName} className="rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2">
                      <span className="text-xs font-semibold text-violet-700">{match.audienceName}</span>
                      {match.reason && (
                        <p className="text-xs text-violet-600/80 mt-0.5">{match.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 bg-slate-50 p-6">
                <label htmlFor="pounce-opener" className="text-sm font-bold text-slate-900">Start a conversation</label>
                <p className="text-xs text-slate-500 mt-1">This immediately opens the visitor’s chat and joins you to the conversation.</p>
                <textarea
                  id="pounce-opener"
                  value={opener}
                  onChange={(event) => setOpener(event.target.value)}
                  maxLength={500}
                  rows={3}
                  className="mt-3 w-full resize-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                {actionError && <p role="alert" className="mt-2 text-sm text-red-600">{actionError}</p>}
                <div className="flex items-center justify-between gap-3 mt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-slate-500"
                    disabled={dismissMutation.isPending || pounceMutation.isPending}
                    onClick={() => dismissMutation.mutate(selected.visitorId)}
                  >
                    <X className="w-4 h-4 mr-2" /> Dismiss
                  </Button>
                  <Button
                    type="button"
                    disabled={!opener.trim() || pounceMutation.isPending || dismissMutation.isPending}
                    onClick={handlePounce}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Radio className="w-4 h-4 mr-2" />
                    {pounceMutation.isPending ? 'Opening chat…' : 'Pounce now'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center p-8 text-center">
            <div>
              <Radio className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="text-sm font-semibold text-slate-600 mt-3">Waiting for a matched visitor</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
